import { useEffect } from 'react';
import api from '@/utils/api';

/* Utility: validate hex colour (#rgb, #rrggbb, #rrggbbaa) */
export const isValidHex = (v) => typeof v === 'string' && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v.trim());

/* Utility: hex -> "r, g, b" for use in rgb(var(--brand-gold-rgb) / alpha) */
const hexToRgbTriplet = (hex) => {
  if (!isValidHex(hex)) return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length === 8) h = h.slice(0, 6);
  const num = parseInt(h, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
};

/* Sanitise a font name (letters, numbers, spaces, hyphen, quote) */
export const isValidFontName = (v) => typeof v === 'string' && /^[A-Za-z0-9 '\-",]+$/.test(v.trim()) && v.length <= 60;

const DEFAULT_BRAND = {
  primary_gold: '#D4AF37',
  pink_accent: '#F3A6C8',
  background: '#080808',
  card_bg: '#121212',
  text_primary: '#FFFFFF',
  text_secondary: '#A0A0A0',
  heading_font: 'Manrope',
  body_font: 'Inter',
};

/* Load a Google Font stylesheet on demand (idempotent per font name). */
const loadedFonts = new Set();
const loadGoogleFont = (family) => {
  if (!family || !isValidFontName(family)) return;
  const key = family.trim();
  if (loadedFonts.has(key)) return;
  loadedFonts.add(key);
  const linkId = `gfont-${key.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(key)}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
};

/* Apply branding CSS variables to :root */
export const applyBranding = (brand) => {
  const b = { ...DEFAULT_BRAND, ...(brand || {}) };
  const root = document.documentElement;

  const setVar = (name, value, fallback) => {
    root.style.setProperty(name, isValidHex(value) ? value : fallback);
  };

  setVar('--brand-gold', b.primary_gold, DEFAULT_BRAND.primary_gold);
  setVar('--brand-pink', b.pink_accent, DEFAULT_BRAND.pink_accent);
  setVar('--brand-bg', b.background, DEFAULT_BRAND.background);
  setVar('--brand-card', b.card_bg, DEFAULT_BRAND.card_bg);
  setVar('--brand-text', b.text_primary, DEFAULT_BRAND.text_primary);
  setVar('--brand-text-secondary', b.text_secondary, DEFAULT_BRAND.text_secondary);

  root.style.setProperty('--brand-gold-rgb', hexToRgbTriplet(b.primary_gold) || hexToRgbTriplet(DEFAULT_BRAND.primary_gold));
  root.style.setProperty('--brand-pink-rgb', hexToRgbTriplet(b.pink_accent) || hexToRgbTriplet(DEFAULT_BRAND.pink_accent));
  root.style.setProperty('--brand-bg-rgb', hexToRgbTriplet(b.background) || hexToRgbTriplet(DEFAULT_BRAND.background));
  root.style.setProperty('--brand-card-rgb', hexToRgbTriplet(b.card_bg) || hexToRgbTriplet(DEFAULT_BRAND.card_bg));

  const headingFont = isValidFontName(b.heading_font) ? b.heading_font.trim() : DEFAULT_BRAND.heading_font;
  const bodyFont = isValidFontName(b.body_font) ? b.body_font.trim() : DEFAULT_BRAND.body_font;
  root.style.setProperty('--brand-heading-font', `'${headingFont}', 'Manrope', sans-serif`);
  root.style.setProperty('--brand-body-font', `'${bodyFont}', 'Inter', sans-serif`);

  if (headingFont && headingFont !== 'Manrope') loadGoogleFont(headingFont);
  if (bodyFont && bodyFont !== 'Inter') loadGoogleFont(bodyFont);
};

/**
 * BrandingProvider — mounts once at app root.
 * Fetches settings.branding once and re-fetches whenever the URL includes ?refresh=branding
 * or on window focus (so admin sees changes after Save).
 */
const BrandingProvider = ({ children }) => {
  useEffect(() => {
    // Apply defaults immediately so first paint has variables set
    applyBranding(DEFAULT_BRAND);

    let mounted = true;
    const fetchAndApply = async () => {
      try {
        const { data } = await api.get('/settings/branding');
        if (mounted) applyBranding(data.value);
      } catch (e) {
        // Fallback silently to defaults
      }
    };
    fetchAndApply();

    const onFocus = () => fetchAndApply();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return children;
};

export default BrandingProvider;
